import { Inject } from '@angular/core';
import templateSource from './view.html';
              import { Component } from '@angular/core';

import Wiz from 'src/wiz';
let wiz = new Wiz('/wiz').app('page.test.measure');
import { OnInit, OnDestroy } from '@angular/core';
import { Service } from "src/libs/portal/season/service";
import { AudioEngine, PitchEvent, midiToNoteName, midiToHz, hzToMidi } from "src/libs/portal/season/audio";

type Step = 'intro' | 'permission' | 'environment' | 'calibrate' | 'descend' | 'ascend' | 'saving' | 'error';

@Component({
    selector: 'wiz-page-test-measure',
template: templateSource || '',
    styles: [`

/* file: /mnt/data/wiz/project/main/build/src/app/page.test.measure/view.scss */
:host {
  display: block;
  height: 100%;
}

@keyframes bounce-once {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.2);
  }
}
.animate-bounce-once {
  animation: bounce-once 0.4s ease-in-out;
}`],
})
export class PageTestMeasureComponent implements OnInit, OnDestroy {
    constructor(@Inject( Service) public service: Service) { }

    public engine: AudioEngine | null = null;
    public step: Step = 'intro';
    public errorMessage: string = '';

    // 환경
    public envQuality: string = '';
    public envScore: number = 0;

    // 실시간 피치 표시
    public currentHz: number = 0;
    public currentMidi: number = 0;
    public currentNote: string = '';
    public currentRms: number = 0;
    public currentConfidence: number = 0;
    public currentCents: number = 0;
    public currentOctave: number = 0;
    public currentNoteNumber: number = 0;
    public isVoice: boolean = false;
    public isInTarget: boolean = false;
    public holdProgress: number = 0;

    // 테스트 상태
    public direction: 'descend' | 'ascend' = 'descend';
    public targetMidi: number = 0;
    public targetNote: string = '';
    public consecutiveFails: number = 0;
    public maxConsecutiveFails: number = 3;
    public noteTimeLimit: number = 10000;
    public noteTimer: any = null;
    public noteTimeLeft: number = 10;
    public successNotes: number[] = [];
    public totalSteps: number = 0;
    public currentStepNum: number = 0;

    // 기준음
    public referenceMidi: number = 57;
    public gender: string = 'male';

    // 보정
    public calibrateDetected: boolean = false;
    public calibrateNote: string = '';
    public calibrateAttempts: number = 0;

    // 애니메이션
    public showSuccess: boolean = false;
    public showFail: boolean = false;

    // 피아노 건반
    public pianoKeys: any[] = [];
    public pianoRangeLow: number = 48; // C3
    public pianoRangeHigh: number = 72; // C5

    public async ngOnInit() {
        await this.service.init();
        this.buildPianoKeys();
        await this.service.render();
    }

    public ngOnDestroy() { this.cleanup(); }

    /* ── 피아노 건반 생성 ── */

    public buildPianoKeys() {
        this.pianoKeys = [];
        for (let midi = this.pianoRangeLow; midi <= this.pianoRangeHigh; midi++) {
            const noteNum = midi % 12;
            const isBlack = [1, 3, 6, 8, 10].includes(noteNum);
            this.pianoKeys.push({
                midi,
                note: midiToNoteName(midi),
                isBlack,
                isC: noteNum === 0
            });
        }
    }

    public updatePianoRange() {
        if (this.targetMidi > 0) {
            this.pianoRangeLow = Math.max(24, this.targetMidi - 12);
            this.pianoRangeHigh = Math.min(96, this.targetMidi + 12);
            this.buildPianoKeys();
        }
    }

    public getPianoKeyClass(key: any): string {
        const isCurrent = this.currentMidi === key.midi && this.currentHz > 0;
        const isTarget = this.targetMidi === key.midi;
        if (key.isBlack) {
            if (isCurrent && isTarget) return 'bg-green-500';
            if (isCurrent) return 'bg-blue-500';
            if (isTarget) return 'bg-indigo-400';
            return 'bg-gray-800';
        }
        if (isCurrent && isTarget) return 'bg-green-300 border-green-400';
        if (isCurrent) return 'bg-blue-200 border-blue-300';
        if (isTarget) return 'bg-indigo-100 border-indigo-300';
        return 'bg-white border-gray-300';
    }

    /* ── 악보 표시용 유틸 ── */

    public getStaffPosition(midi: number): number {
        // C4 = MIDI 60을 기준선(0)으로, 반음마다 위치 계산
        const notePositions: any = { 0: 0, 1: 0, 2: 1, 3: 1, 4: 2, 5: 3, 6: 3, 7: 4, 8: 4, 9: 5, 10: 5, 11: 6 };
        const noteNum = midi % 12;
        const octave = Math.floor(midi / 12) - 1;
        const baseLine = (octave - 4) * 7 + notePositions[noteNum];
        return baseLine;
    }

    public getStaffY(midi: number): number {
        const pos = this.getStaffPosition(midi);
        return 60 - pos * 5; // 중심 60px, 위로 갈수록 감소
    }

    public isSharp(midi: number): boolean {
        return [1, 3, 6, 8, 10].includes(midi % 12);
    }

    public needsLedgerLine(midi: number): number[] {
        const lines: number[] = [];
        const pos = this.getStaffPosition(midi);
        if (pos <= -2) { for (let p = -2; p >= pos; p -= 2) lines.push(60 - p * 5); }
        if (pos >= 10) { for (let p = 10; p <= pos; p += 2) lines.push(60 - p * 5); }
        return lines;
    }

    /* ── 시작 ── */

    public async startTest() {
        this.cleanup();
        this.errorMessage = '';
        this.currentHz = 0;
        this.currentMidi = 0;
        this.currentNote = '';
        this.currentRms = 0;
        this.currentConfidence = 0;
        this.holdProgress = 0;
        this.isVoice = false;
        this.step = 'permission';
        await this.service.render();

        this.engine = new AudioEngine({
            bufferSize: 2048,
            holdDuration: 1000,
            targetTolerance: 1.0,
            minConfidence: 0.65,
            noiseSuppression: true,
            renderThrottle: 50
        });

        try {
            const support = await this.engine.checkBrowserSupport();
            if (!support.supported) {
                this.step = 'error';
                this.errorMessage = '이 브라우저에서는 마이크를 사용할 수 없습니다.\nHTTPS 환경에서 Chrome 또는 Safari를 사용해주세요.';
                this.cleanup();
                await this.service.render();
                return;
            }

            const granted = await this.engine.requestMicrophone();
            if (!granted) {
                this.step = 'error';
                this.errorMessage = '마이크 권한이 필요합니다.\n브라우저 설정에서 마이크를 허용해주세요.';
                this.cleanup();
                await this.service.render();
                return;
            }

            this.step = 'environment';
            await this.service.render();

            const env = await this.engine.measureEnvironment(2000);
            this.envQuality = env.quality === 'quiet' ? '조용함' : env.quality === 'moderate' ? '보통' : '시끄러움';
            this.envScore = env.score;

            this.step = 'calibrate';
            this.calibrateDetected = false;
            this.calibrateNote = '';
            this.calibrateAttempts++;
            await this.service.render();

            this.engine.onPitch((e: PitchEvent) => {
                this.updatePitchDisplay(e);
                if (this.step === 'calibrate' && e.hz > 0 && e.confidence > 0.65) {
                    this.calibrateDetected = true;
                    this.calibrateNote = e.noteName;
                }
                this.service.render();
            });

            this.engine.onNoteHeld((midi: number) => { this.onNoteHeld(midi); });
            await this.engine.start();
        } catch (e) {
            this.step = 'error';
            this.errorMessage = '마이크를 시작하는 중 문제가 발생했습니다.\n입력 장치와 브라우저 권한을 확인한 뒤 다시 시도해주세요.';
            this.cleanup();
            await this.service.render();
        }
    }

    /* ── 보정 확인 → 본 테스트 시작 ── */

    public async confirmCalibrate() {
        if (!this.engine) return;
        this.direction = 'descend';
        this.consecutiveFails = 0;
        this.successNotes = [];
        this.currentStepNum = 0;
        this.targetMidi = this.referenceMidi;
        this.targetNote = midiToNoteName(this.targetMidi);
        this.totalSteps = this.referenceMidi - 36;
        this.step = 'descend';
        this.updatePianoRange();
        this.startNoteTimer();
        this.engine.setTarget(this.targetMidi);
        await this.service.render();
    }

    /* ── 노트 홀드 성공 ── */

    private async onNoteHeld(midi: number) {
        if (midi !== this.targetMidi) return;
        this.successNotes.push(this.targetMidi);
        this.consecutiveFails = 0;
        this.currentStepNum++;
        this.showSuccess = true;
        await this.service.render();
        setTimeout(() => { this.showSuccess = false; this.service.render(); }, 500);
        await this.nextNote();
    }

    /* ── 노트 타이머 (10초) ── */

    private startNoteTimer() {
        this.clearNoteTimer();
        this.noteTimeLeft = 10;
        this.noteTimer = setInterval(async () => {
            this.noteTimeLeft--;
            if (this.noteTimeLeft <= 0) await this.onNoteFail();
            await this.service.render();
        }, 1000);
    }

    private clearNoteTimer() {
        if (this.noteTimer) { clearInterval(this.noteTimer); this.noteTimer = null; }
    }

    public async skipNote() { await this.onNoteFail(); }

    private async onNoteFail() {
        this.consecutiveFails++;
        this.currentStepNum++;
        this.showFail = true;
        await this.service.render();
        setTimeout(() => { this.showFail = false; this.service.render(); }, 500);
        if (this.consecutiveFails >= this.maxConsecutiveFails) {
            if (this.direction === 'descend') { await this.startAscend(); }
            else { await this.finishTest(); }
            return;
        }
        await this.nextNote();
    }

    private async nextNote() {
        if (!this.engine) return;
        this.clearNoteTimer();
        if (this.direction === 'descend') {
            this.targetMidi--;
            if (this.targetMidi < 36) { await this.startAscend(); return; }
        } else {
            this.targetMidi++;
            if (this.targetMidi > 96) { await this.finishTest(); return; }
        }
        this.targetNote = midiToNoteName(this.targetMidi);
        this.engine.setTarget(this.targetMidi);
        this.holdProgress = 0;
        this.updatePianoRange();
        this.startNoteTimer();
        await this.service.render();
    }

    private async startAscend() {
        if (!this.engine) return;
        this.direction = 'ascend';
        this.consecutiveFails = 0;
        this.currentStepNum = 0;
        this.targetMidi = this.referenceMidi + 1;
        this.targetNote = midiToNoteName(this.targetMidi);
        this.totalSteps = 96 - this.referenceMidi;
        this.step = 'ascend';
        this.holdProgress = 0;
        this.updatePianoRange();
        this.engine.setTarget(this.targetMidi);
        this.startNoteTimer();
        await this.service.render();
    }

    private async finishTest() {
        this.clearNoteTimer();
        if (!this.engine) return;
        this.engine.stop();
        this.step = 'saving';
        await this.service.render();
        const result = this.engine.calculateResult(this.successNotes, this.gender);
        const saveData: any = {
            lowest_stable_note: result.lowestNote,
            highest_stable_note: result.highestNote,
            comfortable_low: result.comfortableLow,
            comfortable_high: result.comfortableHigh,
            spoken_range_low: 0,
            spoken_range_high: 0,
            voice_type_estimate: result.voiceType,
            quality_score: result.qualityScore,
            environment_score: result.environmentScore,
            test_mode: 'measure'
        };
        try {
            let { code, data } = await wiz.call("save_result", saveData);
            if (code === 200 && data.id) { this.service.href('/result/' + data.id); return; }
        } catch (e) { }
        this.step = 'error';
        this.errorMessage = '결과 저장에 실패했습니다. 다시 시도해주세요.';
        await this.service.render();
    }

    public async setGender(g: string) {
        this.gender = g;
        this.referenceMidi = g === 'female' ? 69 : 57;
        await this.service.render();
    }

    private updatePitchDisplay(e: PitchEvent) {
        this.currentHz = Math.round(e.hz);
        this.currentMidi = e.midi;
        this.currentNote = e.noteName;
        this.currentRms = Math.min(100, e.rms * 500);
        this.currentConfidence = Math.round(e.confidence * 100);
        this.currentCents = e.cents;
        this.currentOctave = e.octave;
        this.currentNoteNumber = e.noteNumber;
        this.isVoice = e.isVoice;
        this.isInTarget = e.isInTarget;
        this.holdProgress = e.holdProgress;
    }

    public getNoteName(midi: number): string { return midiToNoteName(midi); }
    public getHz(midi: number): number { return Math.round(midiToHz(midi)); }

    public getMeterPosition(): number {
        if (!this.currentMidi || !this.targetMidi) return 50;
        const diff = this.currentMidi - this.targetMidi;
        const clamped = Math.max(-12, Math.min(12, diff));
        return 50 + (clamped / 12) * 50;
    }

    public getTargetZoneTop(): number { return 50 - (1 / 12) * 50; }
    public getTargetZoneHeight(): number { return (2 / 12) * 50; }

    public getCentsText(): string {
        if (!this.currentHz || this.currentCents === 0) return '';
        return this.currentCents > 0 ? `+${this.currentCents}¢` : `${this.currentCents}¢`;
    }

    public async retryCalibration() {
        await this.startTest();
    }

    public async quitTest() {
        let res = await this.service.modal.show({
            title: '측정 종료',
            message: '진행 중인 측정을 종료하시겠습니까?',
            action: '종료',
            cancel: '계속하기',
            status: 'warning',
            actionBtn: 'warning'
        });
        if (!res) return;
        this.cleanup();
        this.service.href('/test');
    }

    public goHome() { this.cleanup(); this.service.href('/'); }
    public goTest() {
        this.cleanup();
        this.step = 'intro';
        this.errorMessage = '';
        this.calibrateDetected = false;
        this.calibrateNote = '';
        this.service.render();
    }

    private cleanup() {
        this.clearNoteTimer();
        if (this.engine) { this.engine.destroy(); this.engine = null; }
    }
}

export default PageTestMeasureComponent;